import type { Breakpoint, SxProps } from '@mui/material';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { Button, type ButtonProps, Typography } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';

export type ConfirmationModalProps = {
  onConfirm: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  title: string;
  open: boolean;
  bodyText?: string;
  children?: React.ReactNode;
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
  deleteButtonLabel?: string;
  maxWidth?: Breakpoint | false;
  loading?: boolean;
  dialogStyles?: {
    dialogContainer?: SxProps;
    dialogContentStyles?: SxProps;
    dialogTitleStyles?: SxProps;
    confirmButton?: SxProps;
    cancelButton?: SxProps;
  };
  confirmButtonColor?: ButtonProps['color'];
  cancelButtonColor?: ButtonProps['color'];
  showCancelButton?: boolean;
  showDeleteButton?: boolean;
  cancelButtonVariant?: ButtonProps['variant'];
  confirmButtonVariant?: ButtonProps['variant'];
  deleteButtonVariant?: ButtonProps['variant'];
  testIdPrefix?: string;
  confirmButtonDisabled?: boolean;
  disableCancelButton?: boolean;
};

export const ConfirmationModal = ({
  onConfirm,
  onCancel,
  children,
  title,
  open,
  bodyText,
  confirmButtonLabel,
  cancelButtonLabel,
  deleteButtonLabel,
  maxWidth = 'sm',
  loading = false,
  dialogStyles,
  confirmButtonColor = 'error',
  cancelButtonColor,
  cancelButtonVariant = 'outlined',
  confirmButtonVariant = 'contained',
  deleteButtonVariant = 'outlined',
  showCancelButton = true,
  onDelete,
  showDeleteButton,
  testIdPrefix = 'confirmation-modal',
  confirmButtonDisabled = false,
  disableCancelButton = loading,
}: ConfirmationModalProps) => {
  const { t } = useTranslation('shared');
  return (
    <Dialog
      data-testid={`${testIdPrefix}-dialog`}
      onClose={onCancel}
      open={open}
      maxWidth={maxWidth}
      fullWidth
      sx={[
        ...(dialogStyles?.dialogContainer &&
        Array.isArray(dialogStyles?.dialogContainer)
          ? dialogStyles.dialogContainer
          : [dialogStyles?.dialogContainer]),
      ]}
    >
      <DialogTitle data-testid={`${testIdPrefix}-title`}>{title}</DialogTitle>

      {(children || bodyText) && (
        <DialogContent
          sx={[
            { minWidth: '400px' },
            ...(dialogStyles?.dialogContentStyles &&
            Array.isArray(dialogStyles?.dialogContentStyles)
              ? dialogStyles.dialogContentStyles
              : [dialogStyles?.dialogContentStyles]),
          ]}
          data-testid={`${testIdPrefix}-content`}
        >
          {children}
          {!children && bodyText && (
            <Typography
              data-testid={`${testIdPrefix}-body-text`}
              variant='bodyLarge'
            >
              {bodyText}
            </Typography>
          )}
        </DialogContent>
      )}
      <DialogFooter data-testid={`${testIdPrefix}-footer`}>
        {showCancelButton && (
          <Button
            type='button'
            variant={cancelButtonVariant}
            color={cancelButtonColor}
            disabled={disableCancelButton}
            onClick={onCancel}
            data-testid={`${testIdPrefix}-cancel-button`}
          >
            {cancelButtonLabel ?? t('shared:cancel')}
          </Button>
        )}

        {showDeleteButton && (
          <Button
            type='button'
            variant={deleteButtonVariant}
            color={cancelButtonColor}
            disabled={loading}
            onClick={onDelete}
            data-testid={`${testIdPrefix}-delete-button`}
          >
            {deleteButtonLabel ?? t('shared:delete')}
          </Button>
        )}

        <Button
          sx={{ ml: 1 }}
          type='button'
          variant={confirmButtonVariant}
          color={confirmButtonColor}
          disabled={loading || confirmButtonDisabled}
          onClick={onConfirm}
          data-testid={`${testIdPrefix}-confirm-button`}
        >
          {confirmButtonLabel ?? t('shared:confirm')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

export default ConfirmationModal;
